<template>
  <div class="integrations-panel">
    <!--START: Header-->
    <PageHeader
      :content="headerContent"
      :showButton="false"
    ></PageHeader>
    <!--END: Header-->

    <!--START: Loader-->
    <PreLoader v-if="loading" :show="true" class="pre-loader"></PreLoader>
    <!--END: Loader-->

    <!--START: Integration Cards-->
    <div v-else class="integrations-grid-wrapper">
      <div
        v-for="(category, categoryName) in groupedProviders"
        :key="categoryName"
        class="category-section"
      >
        <label class="category-label">{{ categoryName }}</label>
        <div class="cards-row">
          <IntegrationCard
            v-for="provider in category"
            :key="provider.key"
            :provider="provider"
            @connect="openConnectModal"
            @disconnect="disconnectProvider"
            @toggle="toggleProvider"
          />
        </div>
      </div>
    </div>
    <!--END: Integration Cards-->

    <!--START: Connect Modal (for providers with form fields)-->
    <ConnectModal
      v-if="showConnectModal"
      :provider="selectedProvider"
      @close="closeConnectModal"
      @connected="onConnected"
    />
    <div
      class="bg-overlay light"
      :class="{ show: showConnectModal }"
      @click="closeConnectModal"
    ></div>
    <!--END: Connect Modal-->

    <!--START: Connect Confirmation (for OAuth providers with no form fields)-->
    <ConfirmationModal
      :fields="connectConfirmFields"
      @primaryEvent="confirmConnect"
      @secondaryEvent="closeConnectConfirm"
    />
    <div
      class="bg-overlay light"
      :class="{ show: connectConfirmFields.show }"
      @click="closeConnectConfirm"
    ></div>
    <!--END: Connect Confirmation-->

    <!--START: Disconnect Confirmation-->
    <ConfirmationModal
      :fields="confirmationFields"
      @primaryEvent="confirmDisconnect"
      @secondaryEvent="closeConfirmationModal"
    />
    <div
      class="bg-overlay light"
      :class="{ show: confirmationFields.show }"
      @click="closeConfirmationModal"
    ></div>
    <!--END: Disconnect Confirmation-->

    <!--START: Notification-->
    <NotificationMessage
      :show="status.show"
      :status="status.status"
      :title="status.title"
    />
    <!--END: Notification-->

    <!--START: Line Loader-->
    <LineLoader :show="lineLoading" />
    <!--END: Line Loader-->
  </div>
</template>

<script>
import _ from "lodash";

import PreLoader from "@/components/loaders/PreLoader";
import LineLoader from "@/components/loaders/LineLoader";
import PageHeader from "@/components/dashboard/PageHeader";
import NotificationMessage from "@/components/modals/NotificationMessage";
import ConfirmationModal from "@/components/modals/ConfirmationModal";

import IntegrationCard from "@/components/dashboard/integrations/IntegrationCard";
import ConnectModal from "@/components/dashboard/integrations/ConnectModal";

import { IntegrationService } from "@/services";

export default {
  name: "IntegrationsPanel",
  data() {
    return {
      lodash: _,
      loading: true,
      lineLoading: false,
      registry: {},
      connected: [],
      showConnectModal: false,
      selectedProvider: null,
      disconnectTarget: null,
      headerContent: {
        title: "Integrations",
        description:
          "Connect external services to extend functionality across all Macha apps",
      },
      connectConfirmFields: {
        show: false,
        title: "Connect Google?",
        type: "warning",
        modalType: "fixed",
        message:
          "You'll be redirected to Google to authorize access to Docs, Sheets, and Drive.",
        primaryBtn: { title: "Continue" },
        secondaryBtn: { title: "Cancel" },
      },
      confirmationFields: {
        show: false,
        title: "Disconnect integration?",
        type: "delete",
        modalType: "fixed",
        message: "This will remove the connection and credentials.",
        iconType: "exclamation",
        confirmAction: true,
        confirmValue: null,
        primaryBtn: { title: "Yes, Disconnect" },
        secondaryBtn: { title: "Cancel" },
      },
      status: {
        show: false,
        status: "success",
        title: "",
      },
    };
  },

  components: {
    PreLoader,
    LineLoader,
    PageHeader,
    NotificationMessage,
    ConfirmationModal,
    IntegrationCard,
    ConnectModal,
  },

  computed: {
    company() {
      return this.$store.getters.company;
    },

    providers() {
      const providers = [];
      for (const [key, config] of Object.entries(this.registry)) {
        const connection = this.connected.find((c) => c.provider === key);
        providers.push({
          key,
          ...config,
          isConnected: !!connection,
          isActive: connection ? connection.isActive : false,
          connectedAt: connection ? connection.connectedAt : null,
          metadata: connection ? connection.metadata : {},
        });
      }
      return providers;
    },

    groupedProviders() {
      const categoryLabels = {
        productivity: "Productivity",
        payments: "Payments",
        ecommerce: "E-Commerce",
      };

      // Pre-seed in display order so JS object key order is correct
      const groups = {};
      for (const label of Object.values(categoryLabels)) {
        groups[label] = [];
      }

      for (const provider of this.providers) {
        const label = categoryLabels[provider.category] || provider.category;
        if (!groups[label]) groups[label] = [];
        groups[label].push(provider);
      }

      // Remove empty groups
      for (const key of Object.keys(groups)) {
        if (groups[key].length === 0) delete groups[key];
      }
      return groups;
    },
  },

  async created() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;

      const [registryRes, listRes] = await Promise.all([
        IntegrationService.GetRegistry(),
        IntegrationService.ListIntegrations(),
      ]);

      if (!registryRes?.hasError) {
        this.registry = registryRes.data || {};
      }
      if (!listRes?.hasError) {
        this.connected = listRes.data || [];
      }

      this.loading = false;
    },

    openConnectModal(provider) {
      this.selectedProvider = provider;

      // OAuth providers with no form fields — use confirmation modal
      const isOAuth = provider.authType === "oauth2";
      const dynamicFields = provider.oauth?.dynamicFields || [];
      const hasFormFields =
        isOAuth
          ? dynamicFields.length > 0
          : (provider.connectFields || []).length > 0;

      if (isOAuth && !hasFormFields) {
        this.connectConfirmFields.title = `Connect ${provider.name}?`;
        this.connectConfirmFields.message = `You'll be redirected to ${provider.name} to authorize access.`;
        this.connectConfirmFields.show = true;
      } else {
        this.showConnectModal = true;
      }
    },

    closeConnectModal() {
      this.showConnectModal = false;
      this.selectedProvider = null;
    },

    async confirmConnect() {
      this.connectConfirmFields.show = false;

      if (!this.selectedProvider) return;

      this.lineLoading = true;
      const response = await IntegrationService.OAuthStart({
        provider: this.selectedProvider.key,
        metadata: {},
      });

      if (response?.hasError) {
        this.showErrorMessage(this.status, response?.message || "Failed to start OAuth");
        this.lineLoading = false;
        this.selectedProvider = null;
        return;
      }

      this.openOAuthPopup(response.data);
    },

    openOAuthPopup(url) {
      const width = 500;
      const height = 650;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      const popup = window.open(
        url,
        "oauth_popup",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
      );

      // Listen for the postMessage from the callback page
      const handler = async (event) => {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "integration_oauth") return;

        window.removeEventListener("message", handler);

        if (data.success) {
          this.showStatusMessage(this.status, 2500, "Integration connected");
          await this.loadData();
        } else {
          this.showErrorMessage(this.status, `Connection failed: ${data.error || "unknown"}`);
        }

        this.lineLoading = false;
        this.selectedProvider = null;
      };

      window.addEventListener("message", handler);

      // Fallback: if popup is closed without completing
      const pollTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener("message", handler);
          this.lineLoading = false;
          this.selectedProvider = null;
        }
      }, 500);
    },

    closeConnectConfirm() {
      this.connectConfirmFields.show = false;
      this.selectedProvider = null;
    },

    async onConnected() {
      this.closeConnectModal();
      this.showStatusMessage(this.status, 2500, "Integration connected");
      await this.loadData();
    },

    disconnectProvider(provider) {
      this.disconnectTarget = provider;
      this.confirmationFields.title = `Disconnect ${provider.name}?`;
      this.confirmationFields.show = true;
    },

    async confirmDisconnect() {
      this.confirmationFields.show = false;

      if (!this.disconnectTarget) return;

      this.lineLoading = true;
      const response = await IntegrationService.Disconnect({
        provider: this.disconnectTarget.key,
      });

      if (!response?.hasError) {
        this.showStatusMessage(this.status, 2500, "Integration disconnected");
        await this.loadData();
      } else {
        this.showErrorMessage(this.status, response?.message);
      }

      this.lineLoading = false;
      this.disconnectTarget = null;
    },

    closeConfirmationModal() {
      this.confirmationFields.show = false;
      this.disconnectTarget = null;
    },

    async toggleProvider({ provider, isActive }) {
      this.lineLoading = true;
      const response = await IntegrationService.Toggle({
        provider: provider.key,
        isActive,
      });

      if (!response?.hasError) {
        this.showStatusMessage(
          this.status,
          2500,
          `${provider.name} ${isActive ? "enabled" : "disabled"}`,
        );
        await this.loadData();
      } else {
        this.showErrorMessage(this.status, response?.message);
      }

      this.lineLoading = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.header-wrapper {
  border-bottom: none;
}

.pre-loader {
  margin: 2rem auto;
  max-width: 60%;
}

.integrations-grid-wrapper {
  padding-top: 0.5rem;
}

.category-section {
  margin-bottom: 2rem;
}

.category-label {
  display: block;
  color: lighten($paragraphColor, 15%);
  font-weight: $mediumFontWeight;
  font-size: $smallestFontSize;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
}

.cards-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
  gap: 1rem;
}

::v-deep .line-loader {
  position: fixed;
  z-index: 101;
  bottom: 0;
  left: 0;
}
</style>
